import { Component } from 'react';
import { GeneratorLayout, genStyles } from '../converter-layout/generator-layout';
import { PersistedInput } from '../../../utils/persisted-input';

const STORAGE_KEY = 'markdown-toc';
type PersistedToc = { input: string; minLevel: number; maxLevel: number; title: string };
const isValidPersisted = (v: unknown): v is PersistedToc =>
    !!v && typeof v === 'object'
    && typeof (v as any).input === 'string'
    && typeof (v as any).minLevel === 'number'
    && typeof (v as any).maxLevel === 'number'
    && typeof (v as any).title === 'string';

interface State {
    input: string;
    minLevel: number;
    maxLevel: number;
    title: string;
    copied: boolean;
}

const SAMPLE = `# My Project

## Installation
### Requirements
### Quick start
### Manual install

## Usage
### Basic example
### Advanced configuration
#### Environment variables
#### Logging

## API Reference
### Endpoints
### Authentication

## Contributing

## License
`;

export default class MarkdownTocGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    private saveTimer: ReturnType<typeof setTimeout> | null = null;

    state: State = (() => {
        const def: PersistedToc = { input: SAMPLE, minLevel: 2, maxLevel: 4, title: 'Table of Contents' };
        const r = PersistedInput.load(STORAGE_KEY, def, isValidPersisted);
        return { ...r, copied: false };
    })();

    componentDidUpdate(_: any, prev: State) {
        const { input, minLevel, maxLevel, title } = this.state;
        if (prev.input !== input || prev.minLevel !== minLevel || prev.maxLevel !== maxLevel || prev.title !== title) {
            if (this.saveTimer) clearTimeout(this.saveTimer);
            this.saveTimer = setTimeout(() => PersistedInput.save(STORAGE_KEY,
                { input, minLevel, maxLevel, title }), 500);
        }
    }

    private slugify(text: string): string {
        return text.toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .trim()
            .replace(/\s+/g, '-');
    }

    private get toc(): string {
        const { input, minLevel, maxLevel, title } = this.state;
        const lines: string[] = [];
        let inFence = false;
        for (const line of input.split('\n')) {
            if (line.trim().startsWith('```')) { inFence = !inFence; continue; }
            if (inFence) continue;
            const m = line.match(/^(#{1,6})\s+(.+?)\s*#*$/);
            if (!m) continue;
            const level = m[1].length;
            if (level < minLevel || level > maxLevel) continue;
            const text = m[2].trim();
            const slug = this.slugify(text);
            const indent = '  '.repeat(level - minLevel);
            lines.push(`${indent}- [${text}](#${slug})`);
        }
        if (lines.length === 0) return '';
        return title ? `## ${title}\n\n${lines.join('\n')}` : lines.join('\n');
    }

    copyOutput = () => {
        const out = this.toc;
        if (!out) return;
        navigator.clipboard.writeText(out).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
        if (this.saveTimer) clearTimeout(this.saveTimer);
    }

    render() {
        const { input, minLevel, maxLevel, title, copied } = this.state;
        const toc = this.toc;
        const lineCount = toc.split('\n').filter(Boolean).length;

        const form = (
            <>
                {/* Markdown input is the primary action — render first so iPhone SE / 320px
                    users see the textarea above the fold instead of scrolling past Options. */}
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Markdown source</span></div>
                    <div className={genStyles.field}>
                        <label htmlFor="toc-md">Paste markdown with headings</label>
                        <textarea id="toc-md" rows={16} value={input}
                            spellCheck={false}
                            onChange={e => this.setState({ input: e.target.value, copied: false })}
                            aria-label="Markdown input" />
                    </div>
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Options</span></div>
                    <div className={genStyles.field}>
                        <label htmlFor="toc-title">TOC heading</label>
                        <input id="toc-title" type="text" value={title}
                            onChange={e => this.setState({ title: e.target.value })} />
                    </div>
                    <div className={genStyles.fieldRow}>
                        <div className={genStyles.field}>
                            <label htmlFor="toc-min">Min depth (H#)</label>
                            <input id="toc-min" type="number" inputMode="decimal" min={1} max={6} value={minLevel}
                                onChange={e => this.setState({ minLevel: Math.min(maxLevel, Math.max(1, +e.target.value || 1)) })} />
                        </div>
                        <div className={genStyles.field}>
                            <label htmlFor="toc-max">Max depth (H#)</label>
                            <input id="toc-max" type="number" inputMode="decimal" min={1} max={6} value={maxLevel}
                                onChange={e => this.setState({ maxLevel: Math.max(minLevel, Math.min(6, +e.target.value || 6)) })} />
                        </div>
                    </div>
                </div>
            </>
        );

        return (
            <GeneratorLayout
                expandable
                form={form}
                previewLabel="Generated TOC"
                previewMeta={`${lineCount} line${lineCount === 1 ? '' : 's'}`}
                preview={<pre className={genStyles.previewArea} aria-label="Generated TOC"
                    style={{ whiteSpace: 'pre', overflowX: 'auto', wordBreak: 'normal' }}>{toc || 'Paste markdown with H1–H6 headings to generate a table of contents.'}</pre>}
                previewActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput} disabled={!toc}>
                        {copied ? '✓ Copied' : '📋 Copy'}
                    </button>
                }
            />
        );
    }
}
